import React, { FC, useEffect, useRef, useState } from "react";
import { Form, Formik, FormikHelpers } from "formik";
import * as Yup from "yup";
import dayjs from "dayjs";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useDispatch, useSelector } from "react-redux";
import { Room, SearchFormValues } from "@/types/Search";
import { RootState } from "@/redux/types/RootState";
import { AppDispatch } from "@/redux/store";
import { setSearchValues } from "@/redux/reducer/initSearchSlice";
import { addNotification } from "@/redux/reducer/notificationSlice";
import { loadOptions } from "@/utils/search/hotelLoadOptions";
import { initHotelSearch } from "@/utils/search/initHotelSearch";
import { useConfig } from "@/context/ConfigContext";
import AutoSuggest from "../selects/AutoSuggest/AutoSuggest";
import DateRangePickerComp from "../Fields/DatePicker/DateRangePicker/DateRangePicker";
import GuestsField from "../Fields/selectFields/GuestsField/GuestsField";
import CustomSearchField from "../Fields/CustomSearchField/CustomSearchField";
import AppButton from "@/components/buttons/AppButton/AppButton";
import { CurrencyIcon, DateIcon, DestinationIcon2 } from "@/components/icons";
import { useMediaQuery } from "@mui/material";
import { useUserJourney } from "@/hooks/useUserJourney";

interface HotelSearchFormProps {
  onCloseEditSearch?: () => void;
  loading?: () => void;
}

interface FormikError {
  [key: string]: string;
}
type DropdownType = "date";

const HotelSearchForm: FC<HotelSearchFormProps> = ({
  onCloseEditSearch,
  loading,
}) => {
  const { config } = useConfig();
  const router = useRouter();
  const dispatch: AppDispatch = useDispatch();
  const searchState = useSelector((state: RootState) => state.hotelSearch);
  const t = useTranslations();
  const currency = useSelector(
    (state: RootState) => state.hotelSearch.currency || config.currency.code
  );
  const isMobile = useMediaQuery("(max-width:576px)");
  const { trackEvent, getFullUrl } = useUserJourney("1231231211");

  const [loadingButton, setLoadingButton] = useState(false);
  const [guestData, setGuestData] = useState<Room[]>(searchState.rooms);

  const today = dayjs().startOf("day");
  const [nights, setNights] = useState(searchState.nights);
  const [startDate, setStartDate] = useState(dayjs(searchState.checkInDate));
  const [endDate, setEndDate] = useState(dayjs(searchState.checkOutDate));

  const [nationality, setNationality] = useState(
    searchState.nationality ? searchState.nationality : config.country.code
  );

  const [errorStyle, setErrorStyle] = useState(false);

  const [dropdowns, setDropdowns] = useState<Record<DropdownType, boolean>>({
    date: false,
  });

  const calendarRef = useRef<HTMLDivElement>(null);
  const guestsRef = useRef<HTMLDivElement>(null);
  const dropdownRefs = {
    nationality: useRef<HTMLDivElement>(null),
    date: useRef<HTMLDivElement>(null),
  };

  const initialCheckInDate =
    searchState.checkInDate && dayjs(searchState.checkInDate).isBefore(today)
      ? today
      : dayjs(searchState.checkInDate);

  const initialCheckOutDate =
    searchState.checkOutDate && dayjs(searchState.checkInDate).isBefore(today)
      ? initialCheckInDate.add(1, "day")
      : dayjs(searchState.checkOutDate);

  useEffect(() => {
    setStartDate(initialCheckInDate);
    setEndDate(initialCheckOutDate);
    setNights(initialCheckOutDate.diff(initialCheckInDate, "day"));
  }, []);

  const initialValues: SearchFormValues = {
    destination: searchState.destination,
    checkInDate: initialCheckInDate,
    checkOutDate: initialCheckOutDate,
    nights: nights,
    nationality: nationality,
    currency: currency,
  };

  const HotelSchema = Yup.object({
    destination: Yup.object({
      label: Yup.string(),
      value: Yup.string().required(t(`searchForm.destinationRequired`)),
      countryCode: Yup.string(),
      country: Yup.string(),
    })
      .nullable()
      .required(t(`searchForm.destinationRequired`)),
  });

  const handleDropdownToggle = (dropdown: DropdownType) => {
    setDropdowns((prev) => {
      const newDropdowns = {
        date: dropdown === "date" ? !prev.date : false,
      };
      return newDropdowns;
    });
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const isClickOutside =
        !calendarRef.current?.contains(event.target as Node) &&
        !guestsRef.current?.contains(event.target as Node) &&
        // !dropdownRefs.nationality.current?.contains(event.target as Node) &&
        !dropdownRefs.date.current?.contains(event.target as Node);

      if (isClickOutside) {
        setDropdowns({
          // nationality: false,
          date: false,
        });
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSubmit = async (
    values: SearchFormValues,
    { setSubmitting }: FormikHelpers<SearchFormValues>
  ) => {
    setSubmitting(true);

    if (!values.destination.value) {
      dispatch(
        addNotification({
          type: "error",
          title: t(`searchForm.destinationRequired`),
          subtitle: t(`searchForm.selectDestination`),
        })
      );
      setErrorStyle(true);
      setSubmitting(false);
      return;
    }

    try {
      setErrorStyle(false);
      setLoadingButton(true);
      if (loading) loading();
      const payload = {
        preferences: {
          checkin: startDate?.format("YYYY-MM-DD") ?? searchState?.checkInDate!,
          nights: nights.toString(),
          nationality: nationality,
          currency: currency,
          nightly_price: false,
        },
        device: isMobile ? "web_mobile" : "web",
        rooms: guestData,
        ...(values.destination.isProperty
          ? { hotel_codes: [values.destination.value] }
          : { destination_code: values.destination.value }),
      };
      const response = await initHotelSearch(
        payload,

        dispatch
      );

      dispatch(
        setSearchValues({
          checkInDate: startDate?.format("YYYY-MM-DD"),
          checkOutDate: endDate?.format("YYYY-MM-DD"),
          nights: nights,
          currency: currency,
          ratings: searchState.ratings,
          destination: values.destination,
          nationality: nationality,
          rooms: guestData,
        })
      );

      if (response.code === 200 && response.response?.sid) {
        const locale = router.locale;
        trackEvent({
          page: router.pathname.replace("/", ""),
          url: getFullUrl(),
          action: "init-hotel-search",
          api_payload: payload,
          api_response: response.response,
          api_error: false,
          sid: response.response.sid,
        });
        router.push(`/search?sid=${response.response.sid}`, undefined, {
          locale: locale,
        });
      } else if (response.code === 2) {
        dispatch(
          addNotification({
            type: "info",
            title: t(`error.Subtitle2`),
            subtitle: response.message,
          })
        );
        setLoadingButton(false);
        trackEvent({
          page: router.pathname.replace("/", ""),
          url: getFullUrl(),
          action: "init-hotel-search",
          api_payload: payload,
          api_response: response,
          api_error: true,
          sid: null,
        });
      } else if (response.code === 598) {
        dispatch(
          addNotification({
            type: "error",
            title: "Connection Error",
            subtitle: response.error,
          })
        );
        setLoadingButton(false);
      } else {
        dispatch(
          addNotification({
            type: "error",
            title: t(`error.SomethingWrong`),
            subtitle: t(`error.Subtitle1`),
          })
        );
        setLoadingButton(false);
        trackEvent({
          page: router.pathname.replace("/", ""),
          url: getFullUrl(),
          action: "init-hotel-search",
          api_payload: payload,
          api_response: response,
          api_error: true,
          sid: null,
        });
      }

      onCloseEditSearch?.();
    } catch {
      dispatch(
        addNotification({
          type: "error",
          title: t(`error.SomethingWrong`),
          subtitle: t(`error.Subtitle2`),
        })
      );
      setLoadingButton(false);
    } finally {
      setSubmitting(false);
      setErrorStyle(false);
    }
  };

  const handleDateRangeChange = (
    start: dayjs.Dayjs | null,
    end: dayjs.Dayjs | null
  ) => {
    if (start && end) {
      const numberOfNights = end.diff(start, "day");
      const maxNights = config.config.limits.room_nights_max;
      setStartDate(start);
      if (numberOfNights > maxNights) {
        setEndDate(start.add(config.config.limits.room_nights_max, "day"));
        setNights(config.config.limits.room_nights_max);
      } else {
        setEndDate(end);
        setNights(numberOfNights);
      }
    }
  };
  const autoSuggestRef = useRef<{ focus: () => void }>(null);

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={async (values, formikHelpers) => {
        const errors: FormikError = {};
        await HotelSchema.validate(values, { abortEarly: false }).catch(
          (err) => {
            err.inner.forEach((validationError: any) => {
              errors[validationError.path as keyof SearchFormValues] =
                validationError.message;
            });
          }
        );

        if (Object.keys(errors).length === 0) {
          await handleSubmit(values, formikHelpers);
          setErrorStyle(false);
        } else {
          formikHelpers.setErrors(errors);
          Object.keys(errors).forEach((key) => {
            dispatch(
              addNotification({
                type: "error",
                title: errors[key],
                subtitle: t(`error.Subtitle1`),
              })
            );
            setErrorStyle(true);
          });
        }
      }}
    >
      {({ setFieldValue, values, isSubmitting }) => (
        <Form className="!h-full !min-h-full !w-full">
          <div className="px-4 justify-center items-center gap-2 flex flex-row h-full">
            <div className="relative w-1/3">
              <CustomSearchField
                key="destination"
                isSearchForm
                icon={<DestinationIcon2 className="text-primary" />}
                onClick={() => autoSuggestRef.current?.focus()}
                isDestination
                name="destination"
                className="!relative cursor-pointer w-full !rounded !border !border-[#d8dcde] !focus:outline-none"
                error={errorStyle}
                content={
                  <AutoSuggest
                    ref={autoSuggestRef}
                    className="w-full"
                    name="destination"
                    placeholder={t(`searchForm.destinationPlaceholder`)}
                    errorStyle={errorStyle}
                    value={values.destination}
                    loadOptions={loadOptions}
                    onOptionSelect={(option) => {
                      setFieldValue("destination", option);
                      setErrorStyle(false);
                      if (option === null) {
                        setFieldValue("destination", {
                          label: "",
                          value: "",
                        });
                      }
                    }}
                    onClick={() => setErrorStyle(false)}
                  />
                }
              />
            </div>

            {/*  <div className="w-full sm:col-span-1 c-md:col-span-1">
              <CustomSearchField
                className="w-full relative"
                isSearchForm
                icon={<Nationality className="c-md:size-6 size-5" />}
                label={t(`searchForm.Nationality`)}
                name="Nationality"
                onClick={() => handleDropdownToggle("nationality")}
                ref={dropdownRefs.nationality}
                content={
                  <CustomSelectSearch
                    options={nationalityOptions}
                    value={nationality}
                    onChange={handleNationalityChange}
                    nationality={true}
                    showOption={true}
                    dropdownIsOpen={dropdowns.nationality}
                    className="w-full"
                    hasArrow
                    hasSearch
                  />
                }
              />
            </div> */}
              <div className="relative c-lg:w-1/3" ref={dropdownRefs.date}>
               
                      <div className="flex flex-col w-full h-[60px]">
                        <div
                          className="flex items-center w-full bg-white border border-solid border-[#d8dcde] h-full rounded"
                          onClick={() => handleDropdownToggle("date")}
                        >
                        <svg className="mx-3" xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 24 24" fill="none" color="#66797F" width="24px" height="24px"><path fill-rule="evenodd" clip-rule="evenodd" d="M17 2.25C17 1.97386 16.7761 1.75 16.5 1.75C16.2239 1.75 16 1.97386 16 2.25V3.25H8V2.25C8 1.97386 7.77614 1.75 7.5 1.75C7.22386 1.75 7 1.97386 7 2.25V3.25H4.5C3.80964 3.25 3.25 3.80964 3.25 4.5V8.25V19.5C3.25 20.1904 3.80964 20.75 4.5 20.75H19.5C20.1904 20.75 20.75 20.1904 20.75 19.5V8.25V4.5C20.75 3.80964 20.1904 3.25 19.5 3.25H17V2.25ZM19.75 7.75V4.5C19.75 4.36193 19.6381 4.25 19.5 4.25H17V5.25C17 5.52614 16.7761 5.75 16.5 5.75C16.2239 5.75 16 5.52614 16 5.25V4.25H8V5.25C8 5.52614 7.77614 5.75 7.5 5.75C7.22386 5.75 7 5.52614 7 5.25V4.25H4.5C4.36193 4.25 4.25 4.36193 4.25 4.5V7.75H19.75ZM4.25 8.75H19.75V19.5C19.75 19.6381 19.6381 19.75 19.5 19.75H4.5C4.36193 19.75 4.25 19.6381 4.25 19.5V8.75Z" fill="currentcolor"></path></svg>

                          <div className="flex-1 mx-">
                            <DateRangePickerComp
                              startDate={startDate}
                              endDate={endDate}
                              onChange={handleDateRangeChange}
                              dropdownIsOpen={dropdowns.date}
                            />
                          </div>
                        </div>
                      
                    </div>
                {/* <CustomSearchField
                  isSearchForm
                  className="!relative !cursor-pointer w-full !rounded !border !border-[#d8dcde] !focus:outline-none"
                  icon={<DateIcon className="text-primary" />}
                  label={t(`searchForm.Date`)}
                  name="date"
                  onClick={() => handleDropdownToggle("date")}
                  ref={dropdownRefs.date}
                  content={
                    <DateRangePickerComp
                      startDate={startDate}
                      endDate={endDate}
                      onChange={handleDateRangeChange}
                      dropdownIsOpen={dropdowns.date}
                    />
                  }
                /> */}
              </div>

              <div className="relative c-lg:w-1/3">
                <GuestsField
                  onGuestChange={setGuestData}
                  guestRooms={guestData}
                  className="w-full h-14 ps-10 !rounded !border !border-[#d8dcde] flex items-center"
                />
              </div>
            {/* <div className="flex gap-4 max-c-lg:flex-col">
            </div>

            <div className="flex gap-4 max-c-lg:flex-col"> */}
              {/* <div className="flex items-center c-lg:w-1/2 h-14 bg-[#FFEFD880] rounded-xl px-2">
                <CurrencyIcon className="text-primary" />

                <span className="font-bold text-sm text-primary mx-4">
                  {t("searchForm.bundleAndSave")}
                </span>

                <label className="flex items-center text-xs">
                  <input
                    type="checkbox"
                    className="me-2 accent-primary !bg-primary !border-primary "
                  />
                  {t("searchForm.addAFlight")}
                </label>
              </div> */}

              <AppButton
                variant="primary"
                label={t("searchForm.search")}
                className="flex w-[135px] h-14 bg-secondary  font-bold text-base !rounded"
                textClassName=" !text-base !text-white"
                type="submit"
                loading={loadingButton}
                disabled={loadingButton || isSubmitting}
                icon={<svg xmlns="http://www.w3.org/2000/svg" role="img" viewBox="0 0 24 24" fill="none" width="24" height="24"><path fill="#fff" fill-rule="evenodd" clip-rule="evenodd" d="M3.49982 10.875C3.49982 6.8019 6.80172 3.5 10.8748 3.5C14.9479 3.5 18.2498 6.8019 18.2498 10.875C18.2498 14.9481 14.9479 18.25 10.8748 18.25C6.80172 18.25 3.49982 14.9481 3.49982 10.875ZM10.8748 2.5C6.24943 2.5 2.49982 6.24962 2.49982 10.875C2.49982 15.5004 6.24943 19.25 10.8748 19.25C13.0074 19.25 14.9538 18.4529 16.4324 17.1404L20.6457 21.3537C20.841 21.549 21.1576 21.549 21.3528 21.3537C21.5481 21.1584 21.5481 20.8419 21.3528 20.6466L17.1396 16.4333C18.4525 14.9547 19.2498 13.0079 19.2498 10.875C19.2498 6.24962 15.5002 2.5 10.8748 2.5Z"></path></svg>}
              />
            </div>
          {/* </div> */}

          {/* <div className=" w-full mt-4">
            <div className="border-t border-[#e5e7eb]" />
            <div className="w-full h-[52px] flex items-center justify-center">
              <p className="font-medium text-sm text-[#4f6f8f] text-center">
                {t("searchForm.footerNote")}
              </p>
            </div>
          </div> */}
        </Form>
      )}
    </Formik>
  );
};

export default HotelSearchForm;

// import React, { FC, useEffect, useRef, useState } from "react";
// import { Form, Formik, FormikHelpers } from "formik";
// import * as Yup from "yup";
// import dayjs from "dayjs";
// import { useRouter } from "next/router";
// import { useTranslations } from "next-intl";
// import { useDispatch, useSelector } from "react-redux";
// import { useMediaQuery } from "@mui/material";

// import { Room, SearchFormValues } from "@/types/Search";
// import { RootState } from "@/redux/types/RootState";
// import { AppDispatch } from "@/redux/store";
// import { setSearchValues } from "@/redux/reducer/initSearchSlice";
// import { addNotification } from "@/redux/reducer/notificationSlice";
// import { loadOptions } from "@/utils/search/hotelLoadOptions";
// import { initHotelSearch } from "@/utils/search/initHotelSearch";
// import { useConfig } from "@/context/ConfigContext";

// import AutoSuggest from "../selects/AutoSuggest/AutoSuggest";
// import DateRangePickerComp from "../Fields/DatePicker/DateRangePicker/DateRangePicker";
// import GuestsField from "../Fields/selectFields/GuestsField/GuestsField";
// import { useUserJourney } from "@/hooks/useUserJourney";
// import { HotelIcon } from "@/components/icons";

// interface FormikError {
//   [key: string]: string;
// }
// type DropdownType = "date";

// export const HotelSearchForm: FC = () => {
//   const [activeTab, setActiveTab] = useState<
//     "flights" | "stays" | "activities"
//   >("stays");

//   const tabs = [
//     { id: "flights", label: "Flights", icon: <></> },
//     { id: "stays", label: "Stays", icon: <HotelIcon className="text-primary" /> },
//     { id: "activities", label: "Activities", icon: <></> },
//   ] as const;

//   // ------------------ functionality (from HotelSearchForm) ------------------
//   const { config } = useConfig();
//   const router = useRouter();
//   const dispatch: AppDispatch = useDispatch();
//   const searchState = useSelector((state: RootState) => state.hotelSearch);
//   const t = useTranslations();
//   const isMobile = useMediaQuery("(max-width:576px)");
//   const { trackEvent, getFullUrl } = useUserJourney("1231231211");

//   const currency = useSelector(
//     (state: RootState) => state.hotelSearch.currency || config.currency.code
//   );

//   const [loadingButton, setLoadingButton] = useState(false);
//   const [guestData, setGuestData] = useState<Room[]>(searchState.rooms);

//   const today = dayjs().startOf("day");
//   const [nights, setNights] = useState(searchState.nights);
//   const [startDate, setStartDate] = useState(dayjs(searchState.checkInDate));
//   const [endDate, setEndDate] = useState(dayjs(searchState.checkOutDate));

//   const [nationality] = useState(
//     searchState.nationality ? searchState.nationality : config.country.code
//   );

//   const [errorStyle, setErrorStyle] = useState(false);

//   const [dropdowns, setDropdowns] = useState<Record<DropdownType, boolean>>({
//     date: false,
//   });

//   const calendarRef = useRef<HTMLDivElement>(null);
//   const guestsRef = useRef<HTMLDivElement>(null);
//   const dropdownRefs = {
//     date: useRef<HTMLDivElement>(null),
//   };

//   const initialCheckInDate =
//     searchState.checkInDate && dayjs(searchState.checkInDate).isBefore(today)
//       ? today
//       : dayjs(searchState.checkInDate);

//   const initialCheckOutDate =
//     searchState.checkOutDate && dayjs(searchState.checkInDate).isBefore(today)
//       ? initialCheckInDate.add(1, "day")
//       : dayjs(searchState.checkOutDate);

//   useEffect(() => {
//     setStartDate(initialCheckInDate);
//     setEndDate(initialCheckOutDate);
//     setNights(initialCheckOutDate.diff(initialCheckInDate, "day"));
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const initialValues: SearchFormValues = {
//     destination: searchState.destination,
//     checkInDate: initialCheckInDate,
//     checkOutDate: initialCheckOutDate,
//     nights: nights,
//     nationality: nationality,
//     currency: currency,
//   };

//   const HotelSchema = Yup.object({
//     destination: Yup.object({
//       label: Yup.string(),
//       value: Yup.string().required(t(`searchForm.destinationRequired`)),
//       countryCode: Yup.string(),
//       country: Yup.string(),
//     })
//       .nullable()
//       .required(t(`searchForm.destinationRequired`)),
//   });

//   const handleDropdownToggle = (dropdown: DropdownType) => {
//     setDropdowns((prev) => ({
//       date: dropdown === "date" ? !prev.date : false,
//     }));
//   };

//   useEffect(() => {
//     const handleClickOutside = (event: MouseEvent) => {
//       const isClickOutside =
//         !calendarRef.current?.contains(event.target as Node) &&
//         !guestsRef.current?.contains(event.target as Node) &&
//         !dropdownRefs.date.current?.contains(event.target as Node);

//       if (isClickOutside) {
//         setDropdowns({ date: false });
//       }
//     };

//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleSubmit = async (
//     values: SearchFormValues,
//     { setSubmitting }: FormikHelpers<SearchFormValues>
//   ) => {
//     setSubmitting(true);

//     if (!values.destination?.value) {
//       dispatch(
//         addNotification({
//           type: "error",
//           title: t(`searchForm.destinationRequired`),
//           subtitle: t(`searchForm.selectDestination`),
//         })
//       );
//       setErrorStyle(true);
//       setSubmitting(false);
//       return;
//     }

//     try {
//       setErrorStyle(false);
//       setLoadingButton(true);

//       const payload = {
//         preferences: {
//           checkin: startDate?.format("YYYY-MM-DD") ?? searchState?.checkInDate!,
//           nights: nights.toString(),
//           nationality: nationality,
//           currency: currency,
//           nightly_price: false,
//         },
//         device: isMobile ? "web_mobile" : "web",
//         rooms: guestData,
//         ...(values.destination.isProperty
//           ? { hotel_codes: [values.destination.value] }
//           : { destination_code: values.destination.value }),
//       };

//       const response = await initHotelSearch(payload, dispatch);

//       dispatch(
//         setSearchValues({
//           checkInDate: startDate?.format("YYYY-MM-DD"),
//           checkOutDate: endDate?.format("YYYY-MM-DD"),
//           nights: nights,
//           currency: currency,
//           ratings: searchState.ratings,
//           destination: values.destination,
//           nationality: nationality,
//           rooms: guestData,
//         })
//       );

//       if (response.code === 200 && response.response?.sid) {
//         const locale = router.locale;

//         trackEvent({
//           page: router.pathname.replace("/", ""),
//           url: getFullUrl(),
//           action: "init-hotel-search",
//           api_payload: payload,
//           api_response: response.response,
//           api_error: false,
//           sid: response.response.sid,
//         });

//         router.push(`/search?sid=${response.response.sid}`, undefined, {
//           locale: locale,
//         });
//       } else {
//         dispatch(
//           addNotification({
//             type: "error",
//             title: t(`error.SomethingWrong`),
//             subtitle: response.message || t(`error.Subtitle1`),
//           })
//         );
//       }
//     } catch {
//       dispatch(
//         addNotification({
//           type: "error",
//           title: t(`error.SomethingWrong`),
//           subtitle: t(`error.Subtitle2`),
//         })
//       );
//     } finally {
//       setSubmitting(false);
//       setLoadingButton(false);
//       setErrorStyle(false);
//     }
//   };

//   const handleDateRangeChange = (
//     start: dayjs.Dayjs | null,
//     end: dayjs.Dayjs | null
//   ) => {
//     if (start && end) {
//       const numberOfNights = end.diff(start, "day");
//       const maxNights = config.config.limits.room_nights_max;

//       setStartDate(start);

//       if (numberOfNights > maxNights) {
//         setEndDate(start.add(maxNights, "day"));
//         setNights(maxNights);
//       } else {
//         setEndDate(end);
//         setNights(numberOfNights);
//       }
//     }
//   };

//   const autoSuggestRef = useRef<{ focus: () => void }>(null);

//   // ------------------ UI (your Frame design) ------------------
//   return (
//     <div className="flex flex-col w-[1520.8px] h-[400px] items-center gap-[38px] relative">
//       <header className="flex flex-wrap w-[1200px] items-start pt-16 pb-0 px-4 relative">
//         <div className="flex flex-col w-[817.6px] items-start gap-2 relative self-stretch">
//           <h1 className="relative flex items-center justify-center self-stretch mt-[-1.00px] font-bold text-white text-[34px] leading-[52px]">
//             Your next adventure starts now
//           </h1>

//           <p className="relative flex items-center justify-center self-stretch font-semibold text-white text-lg leading-7">
//             Choose from over 1.5 million hotels &amp; 450+ airline
//           </p>
//         </div>
//       </header>

//       <div className="flex flex-col w-[1248px] items-start relative">
//         {/* Tabs */}
//         <nav
//           className="flex items-start relative self-stretch w-full"
//           role="tablist"
//           aria-label="Booking options"
//         >
//           {tabs.map((tab) => (
//             <div
//               key={tab.id}
//               className="inline-flex flex-col min-w-[132px] min-h-12 items-start pl-0 pr-0.5 py-0 relative"
//             >
//               <button
//                 role="tab"
//                 aria-selected={activeTab === tab.id}
//                 aria-controls={`${tab.id}-panel`}
//                 id={`${tab.id}-tab`}
//                 onClick={() => setActiveTab(tab.id)}
//                 className={`gap-[3.03px]
//                 ${
//                   tab.id === "flights"
//                     ? "pl-[21.89px] pr-[26.86px] py-[7.03px]"
//                     : tab.id === "stays"
//                     ? "px-[30.16px] py-[11px]"
//                     : "pl-[18.31px] pr-[18.33px] py-3"
//                 }
//                 ${
//                   activeTab === tab.id
//                     ? "bg-white border border-solid"
//                     : "bg-[#00000066]"
//                 }
//                 inline-flex min-w-[130px] min-h-12 items-center justify-center relative rounded-[8px_8px_0px_0px] overflow-hidden`}
//               >
//                 {/* icons (keep same) */}
//                 {tab.id === "flights" && (
//                   <div className="inline-flex flex-col h-[33.94px] items-center justify-center">
//                     <div className="-rotate-45 relative w-6 h-6">
//                       {tab.icon}
                      
//                     </div>
//                   </div>
//                 )}

//                 {tab.id === "stays" && (
//                   <div className="flex flex-col items-start pl-0 pr-2 py-0 w-8 h-6 relative">
//                     <div className="flex flex-col items-center justify-center w-6 h-6">
//                       <div className="relative flex-1 w-6">
//                         {/* <img
//                           className="absolute w-[81.25%] h-[62.50%] top-[16.67%] left-[7.29%]"
//                           alt=""
//                           src={tab.icon}
//                         /> */}
//                       </div>
//                     </div>
//                   </div>
//                 )}

//                 {tab.id === "activities" && (
//            <>{tab.icon}</> 
//                 )}

//                 <span
//                   className={`font-semibold ${
//                     activeTab === tab.id ? "text-[#003143]" : "text-white"
//                   } text-sm`}
//                 >
//                   {tab.label}
//                 </span>
//               </button>
//             </div>
//           ))}
//         </nav>

//         {/* Panel */}
//         <div
//           className="flex flex-col items-start p-6 relative self-stretch w-full -mt-px bg-white rounded-[0px_8px_8px_8px]"
//           role="tabpanel"
//           id={`${activeTab}-panel`}
//           aria-labelledby={`${activeTab}-tab`}
//         >
//           {/* Only Stays tab has hotel search functionality */}
//           {activeTab !== "stays" ? (
//             <div className="py-6 text-center text-gray-600">
//               Coming soon...
//             </div>
//           ) : (
//             <Formik
//               initialValues={initialValues}
//               onSubmit={async (values, formikHelpers) => {
//                 const errors: FormikError = {};

//                 await HotelSchema.validate(values, {
//                   abortEarly: false,
//                 }).catch((err) => {
//                   err.inner.forEach((validationError: any) => {
//                     errors[validationError.path as keyof SearchFormValues] =
//                       validationError.message;
//                   });
//                 });

//                 if (Object.keys(errors).length === 0) {
//                   await handleSubmit(values, formikHelpers);
//                   setErrorStyle(false);
//                 } else {
//                   formikHelpers.setErrors(errors);
//                   Object.keys(errors).forEach((key) => {
//                     dispatch(
//                       addNotification({
//                         type: "error",
//                         title: errors[key],
//                         subtitle: t(`error.Subtitle1`),
//                       })
//                     );
//                     setErrorStyle(true);
//                   });
//                 }
//               }}
//             >
//               {({ setFieldValue, values, isSubmitting }) => (
//                 <Form className="flex items-start justify-center self-stretch w-full">
//                   <div className="inline-flex items-start justify-center gap-2">
//                     {/* Destination */}
//                     <div className="flex flex-col w-[343.54px]">
//                       <div className="px-3.5 py-0 rounded flex items-center w-full bg-white border border-solid border-[#d8dcde] h-14">
//                         {/* <img
//                           className="w-6 h-6 me-2"
//                           alt=""
//                           src={vector2}
//                         /> */}

//                         <div className="flex-1">
//                           <AutoSuggest
//                             ref={autoSuggestRef}
//                             className="w-full"
//                             name="destination"
//                             placeholder={t(`searchForm.destinationPlaceholder`)}
//                             errorStyle={errorStyle}
//                             value={values.destination}
//                             loadOptions={loadOptions}
//                             onOptionSelect={(option) => {
//                               setFieldValue("destination", option);
//                               setErrorStyle(false);
//                               if (option === null) {
//                                 setFieldValue("destination", {
//                                   label: "",
//                                   value: "",
//                                 });
//                               }
//                             }}
//                             onClick={() => setErrorStyle(false)}
//                           />
//                         </div>
//                       </div>
//                     </div>

//                     {/* Date Range */}
//                     <div
//                       className="inline-flex items-start justify-center"
//                       ref={dropdownRefs.date}
//                     >
//                       <div className="flex flex-col w-[426px]">
//                         <div
//                           className="flex items-center w-full bg-white border border-solid border-[#d8dcde] h-14 rounded"
//                           onClick={() => handleDropdownToggle("date")}
//                         >
//                           {/* <img
//                             className="w-6 h-6 mx-3.5"
//                             alt=""
//                             src={vector3}
//                           /> */}

//                           <div className="flex-1">
//                             <DateRangePickerComp
//                               startDate={startDate}
//                               endDate={endDate}
//                               onChange={handleDateRangeChange}
//                               dropdownIsOpen={dropdowns.date}
//                             />
//                           </div>
//                         </div>
//                       </div>
//                     </div>

//                     {/* Guests */}
//                     <div className="flex flex-col w-[274px]" ref={guestsRef}>
//                       <div className="flex items-center w-full bg-white border border-solid border-[#d8dcde] h-14 rounded px-3.5">
//                         {/* <img className="w-6 h-6 me-2" alt="" src={vector4} /> */}

//                         <GuestsField
//                           onGuestChange={setGuestData}
//                           guestRooms={guestData}
//                           className="w-full h-14 !border-0 !shadow-none"
//                         />
//                       </div>
//                     </div>

//                     {/* Search Button */}
//                     <button
//                       type="submit"
//                       disabled={loadingButton || isSubmitting}
//                       className="flex w-[133px] h-[55px] items-center justify-center px-6 py-3 bg-[#ef4550] rounded disabled:opacity-60"
//                       aria-label="Search"
//                     >
//                       {/* <img className="w-6 h-6 me-2" alt="" src={vector5} /> */}
//                       <span className="font-semibold text-white text-base">
//                         {loadingButton ? "Loading..." : "Search"}
//                       </span>
//                     </button>
//                   </div>
//                 </Form>
//               )}
//             </Formik>
//           )}
//         </div>
//       </div>
//     </div>
//   );
// };
